import React, { useCallback, useEffect, useState } from "react";
import { X, Pause, Play, ChevronRight, ChevronLeft } from "lucide-react";
import { photoUrl } from "./PhotoWall";

const when = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

// Full-screen Ken Burns slideshow of cheer photos. Tap = pause/play, arrows = next/prev, Esc = exit.
const Slideshow = ({ photos, onClose, startIndex = 0 }) => {
  const [i, setI] = useState(startIndex);
  const [paused, setPaused] = useState(false);
  const next = useCallback(() => setI((n) => (n + 1) % photos.length), [photos.length]);
  const prev = useCallback(() => setI((n) => (n - 1 + photos.length) % photos.length), [photos.length]);

  useEffect(() => {
    if (paused || photos.length < 2) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [paused, next, photos.length]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); } };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [next, prev, onClose]);

  if (!photos.length) return null;
  const p = photos[i];
  return (
    <div className="fixed inset-0 z-[100] bg-black text-white select-none" onClick={() => setPaused((x) => !x)} data-testid="slideshow" data-index={i} data-paused={paused ? "true" : "false"}>
      <img key={p.id} src={photoUrl(p.id, "full")} alt={`${p.author_name} for ${p.child_name}`} className="absolute inset-0 w-full h-full object-contain ken-burns" />
      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 bg-gradient-to-t from-black/80 to-transparent">
        <p className="text-2xl sm:text-4xl font-bold drop-shadow" style={{ fontFamily: "Literata, serif" }} data-testid="slideshow-caption">“{p.text}”</p>
        <p className="mt-2 text-white/80 text-sm sm:text-base">{p.author_name} · for {p.child_name} · {when(p.created_at)}</p>
        <div className="mt-3 flex gap-1">{photos.map((x, k) => <span key={x.id} className={`h-1.5 rounded-full transition-all ${k === i ? "w-8 bg-white" : "w-3 bg-white/40"}`} />)}</div>
      </div>
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center" aria-label="Close slideshow" data-testid="slideshow-close"><X /></button>
      <button onClick={(e) => { e.stopPropagation(); setPaused((x) => !x); }} className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center" aria-label={paused ? "Play" : "Pause"} data-testid="slideshow-toggle">{paused ? <Play size={18} /> : <Pause size={18} />}</button>
      {photos.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center" aria-label="Previous" data-testid="slideshow-prev"><ChevronLeft /></button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center" aria-label="Next" data-testid="slideshow-next"><ChevronRight /></button>
        </>
      )}
    </div>
  );
};

export default Slideshow;
